function parseEnvMs(raw: string | undefined, fallback: number): number {
  if (raw === undefined || raw === '') return fallback;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

export { parseEnvMs };

/** Hold IPv4 host candidates when ICE_PREFER_IPV6 is on. 0 = send immediately (fast LAN). */
export const IPV4_CANDIDATE_DELAY_MS = parseEnvMs(
  import.meta.env.VITE_ICE_IPV4_DELAY_MS as string | undefined,
  0
);

/** Hold IPv4 srflx (STUN) candidates; IPv6 srflx uses VITE_ICE_SRFLX_V6_DELAY_MS. */
export const SRFLX_IPV4_DELAY_MS = parseEnvMs(
  import.meta.env.VITE_ICE_SRFLX_V4_DELAY_MS as string | undefined,
  0
);

export const SRFLX_IPV6_DELAY_MS = parseEnvMs(
  import.meta.env.VITE_ICE_SRFLX_V6_DELAY_MS as string | undefined,
  0
);

/** Send IPv6 host immediately; delay IPv4/srflx per env above. */
export const ICE_PREFER_IPV6 = (() => {
  const raw = import.meta.env.VITE_ICE_PREFER_IPV6 as string | undefined;
  if (raw === undefined || raw === '') return true;
  return raw === '1' || raw.toLowerCase() === 'true';
})();

/** Milliseconds of P2P inactivity before the PWA closes WebRTC (both ends). 0 = disabled. */
export const P2P_IDLE_MS = (() => {
  const raw = import.meta.env.VITE_P2P_IDLE_MS as string | undefined;
  if (raw === undefined || raw === '') return 30_000;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : 30_000;
})();

/** Signed-in inactivity before full logout (closes signaling WS). 0 = disabled. Default 30 min. */
export const SESSION_IDLE_MS = parseEnvMs(
  import.meta.env.VITE_SESSION_IDLE_MS as string | undefined,
  30 * 60 * 1000
);

/** Max wait for folder listing after P2P is linked before returning to drive/folder pickers. */
export const LIST_FILES_TIMEOUT_MS = parseEnvMs(
  import.meta.env.VITE_LIST_FILES_TIMEOUT_MS as string | undefined,
  45_000
);

/** Max wait to reach linked P2P after opening a share before leaving the explorer shell. */
export const EXPLORER_CONNECT_TIMEOUT_MS = parseEnvMs(
  import.meta.env.VITE_EXPLORER_CONNECT_TIMEOUT_MS as string | undefined,
  60_000
);

/**
 * How many times a single ICE phase is attempted before the next phase runs.
 * 1 = one try per phase (old behaviour), 2 = one retry with a fresh peer.
 *
 * A retry is useful because a brand new RTCPeerConnection produces fresh
 * candidates (new STUN srflx / TURN relay mappings), which a stalled
 * connection cannot refresh on its own.
 */
export const DIAL_ATTEMPTS_PER_PHASE = (() => {
  const raw = import.meta.env.VITE_DIAL_ATTEMPTS_PER_PHASE as string | undefined;
  if (raw === undefined || raw === '') return 2;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 1 ? n : 2;
})();

/** Phase 1 dial budget (STUN-only servers). */
export const STUN_PHASE_TIMEOUT_MS = parseEnvMs(
  import.meta.env.VITE_STUN_PHASE_TIMEOUT_MS as string | undefined,
  22_000
);

/** Phase 2 dial budget (full ICE list including TURN). 0 = no limit in tryDial timer. */
export const TURN_PHASE_TIMEOUT_MS = parseEnvMs(
  import.meta.env.VITE_TURN_PHASE_TIMEOUT_MS as string | undefined,
  45_000
);

/** Pause after p2p-release before next dial phase (ms). */
export const DIAL_PHASE_GAP_MS = parseEnvMs(
  import.meta.env.VITE_DIAL_PHASE_GAP_MS as string | undefined,
  800
);

/** Min wait after p2p-release before the PWA sends the next WebRTC offer.
 *  Align with VITE_HOST_P2P_SETTLE_MS so the host finishes closing the old peer first. */
export const P2P_OFFER_QUIET_MS = parseEnvMs(
  import.meta.env.VITE_P2P_OFFER_QUIET_MS as string | undefined,
  1500
);
